import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react'

import type { AuthUser, CodeSent } from './authApi'
import type { AuthManager, AuthStatus } from './authManager'

export interface Auth {
  status: AuthStatus
  user: AuthUser | null
  email: string | null
  /** Emails a sign-in code; resolves with when it expires and when another may be asked for (seconds). */
  startSignIn: (email: string) => Promise<CodeSent>
  /** Signs in with the emailed code. [deletionCancelled]: this sign-in called off a pending account deletion. */
  verifyCode: (email: string, code: string) => Promise<{ user: AuthUser; deletionCancelled: boolean }>
  signOut: () => Promise<void>
  /** A valid access token, refreshed first when fewer than 60 s remain. */
  getAccessToken: () => Promise<string>
  /** A newer token than one the API answered 401 to (used by the API client). */
  refreshAccessToken: (rejected: string) => Promise<string>
}

const AuthContext = createContext<Auth | null>(null)

export function AuthProvider(props: { manager: AuthManager; children: ReactNode }) {
  const { manager, children } = props
  const state = useSyncExternalStore(manager.subscribe, manager.getState)

  // Signing in or out in another tab rewrites the shared localStorage key; follow it here.
  useEffect(() => {
    window.addEventListener('storage', manager.handleStorageEvent)
    return () => window.removeEventListener('storage', manager.handleStorageEvent)
  }, [manager])

  const value = useMemo<Auth>(
    () => ({
      status: state.status,
      user: state.user,
      email: state.user?.email ?? null,
      startSignIn: manager.startSignIn,
      verifyCode: manager.verifyCode,
      signOut: manager.signOut,
      getAccessToken: manager.getAccessToken,
      refreshAccessToken: manager.refreshAccessToken,
    }),
    [state, manager],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): Auth {
  const auth = useContext(AuthContext)
  if (!auth) throw new Error('useAuth must be used inside AuthProvider')
  return auth
}
